import React from "react";

import { TimingOptions } from "api/model/dashboard/DashboardReportOptions";
import { useCronExpression } from "hooks/useCronExpression";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";
import { dweenTimeUnitOptions, dweenTimeUnitOptionsWithFourFourFive } from "utils/constants";
import { Leaves, setObjAttribute } from "utils/utils";
import { Input } from "../InputCategories";

type TimingOptionsKey = Leaves<TimingOptions>;

const TimingOptionsSubForm = (props: {
  timingOptions: TimingOptions;
  onChange: (o: TimingOptions) => void;
}) => {
  const { cronExp, setCronExp, summary, isValid } = useCronExpression(props.timingOptions.generationCron);

  const onChangeTimingOptions = (prop: TimingOptionsKey, value: any) => {
    const newTimingOptions = { ...props.timingOptions };
    setObjAttribute(newTimingOptions, prop, value);
    props.onChange(newTimingOptions);
  };

  const handleCronChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    onChangeTimingOptions("generationCron", value);
    setCronExp(value);
  };

  const isValidCron = !cronExp ? true : isValid;

  return (
    <>
      <h5 className="mb-5">{T("word.timing-options")}</h5>
      <div className="grid">
        <span className="col-12 md:col-12 mb-4">
          <span className="p-float-label">
            <InputText
              id="input-timing-options-gen-cron"
              type="text"
              className="p-inputtext-sm w-full"
              placeholder={T("label.generation-cron-quartz")}
              value={cronExp}
              invalid={!isValidCron}
              onChange={handleCronChange}
            />
            <label htmlFor="input-timing-options-gen-cron">{T("label.generation-cron-quartz")}</label>
          </span>
          <small className={`pl-3 ${isValidCron ? "text-green-700" : "p-error"}`}>{summary}</small>
        </span>

        <span className="p-float-label col-12 md:col-4 mb-4">
          <Input.List
            id="input-timing-options-truncate-to"
            options={dweenTimeUnitOptionsWithFourFourFive}
            label={T("label.reference-time-truncate-align-unit")}
            property="referenceTimeTruncateTo"
            sourceObj={props.timingOptions}
            onChange={onChangeTimingOptions}
          />
        </span>

        <span className="p-float-label col-12 md:col-4 mb-4">
          <Input.Number
            id="input-timing-options-offset"
            label={T("label.reference-time-offset")}
            property="referenceTimeOffset"
            sourceObj={props.timingOptions}
            onChange={onChangeTimingOptions}
          />
        </span>

        <span className="p-float-label col-12 md:col-4 mb-4">
          <Input.List
            id="input-timing-options-offset-unit"
            options={dweenTimeUnitOptions}
            label={T("label.reference-time-offset-unit")}
            property="referenceTimeOffsetUnit"
            sourceObj={props.timingOptions}
            onChange={onChangeTimingOptions}
          />
        </span>

        <span className="p-float-label col-12 md:col-8">
          <Input.Text
            id="input-timing-options-datetime-format"
            label={T("label.date-time-display-format")}
            property="reportTimeFormat"
            sourceObj={props.timingOptions}
            onChange={onChangeTimingOptions}
          />
        </span>
        <span className="p-float-label col-12 md:col-4">
          <Dropdown
            id="input-timing-options-timezone-id"
            value={props.timingOptions?.timeZoneId}
            options={Intl.supportedValuesOf("timeZone")}
            filter
            placeholder={T("word.select-one")}
            className="w-full"
            onChange={e => onChangeTimingOptions("timeZoneId", e.target.value)}
          />
          <label htmlFor="input-timing-options-timezone-id">{T("load-conf.timezone-id")}</label>
        </span>
      </div>
    </>
  );
};

export default TimingOptionsSubForm;
